import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Camera, Loader2, CheckCircle2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { BIZ } from "@/content";

const KEY = "plumberoo_chat_session";
const QUICK = ["I have a blocked drain", "My hot water isn't working", "Emergency – burst pipe", "I'd like a quote"];
const WELCOME = { role: "assistant", content: "G'day! I'm Roo, Plumberoo's assistant. Ask me any plumbing or gas question, or tell me what's going on and I'll get a plumber onto it." };

const sessionId = () => {
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^a-zA-Z0-9-]/g, "");
    localStorage.setItem(KEY, id);
  }
  return id;
};

const clean = (t) => (t || "").replace(/\*\*/g, "");

const Bubble = ({ m }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-[0.95rem] leading-relaxed ${m.role === "user" ? "rounded-br-md bg-[#0F2B5B] text-white" : "rounded-bl-md bg-white text-slate-800 shadow-sm"}`}>
      {m.image && <img src={m.image} alt="Your attached photo" className="mb-2 max-h-48 w-full rounded-xl object-cover" />}
      {clean(m.content) || <span className="inline-flex gap-1"><span className="h-2 w-2 animate-bounce rounded-full bg-slate-400" /><span className="h-2 w-2 animate-bounce rounded-full bg-slate-400 [animation-delay:120ms]" /><span className="h-2 w-2 animate-bounce rounded-full bg-slate-400 [animation-delay:240ms]" /></span>}
    </div>
  </div>
);

const useChat = () => {
  const [messages, setMessages] = useState([WELCOME]);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const loaded = useRef(false);

  const load = async () => {
    if (loaded.current) return;
    loaded.current = true;
    try {
      const { data } = await api.get(`/chat/${sessionId()}`);
      const msgs = Array.isArray(data?.messages) ? data.messages : [];
      if (msgs.length) setMessages([WELCOME, ...msgs]);
      setSubmitted(!!data?.enquiry_submitted);
    } catch { /* fresh chat */ }
  };

  const patchLast = (fn) => setMessages((l) => { const c = [...l]; c[c.length - 1] = fn(c[c.length - 1]); return c; });

  const send = async (text, image) => {
    if (!text.trim() || busy) return;
    setBusy(true);
    setMessages((l) => [...l, { role: "user", content: text, image }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/stream`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId(), message: text }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        parts.forEach((p) => {
          if (!p.startsWith("data: ")) return;
          const ev = JSON.parse(p.slice(6));
          if (ev.type === "delta" || ev.type === "error") patchLast((m) => ({ ...m, content: m.content + ev.content }));
          if (ev.type === "enquiry" && ev.ok) setSubmitted(true);
        });
      }
    } catch {
      patchLast((m) => ({ ...m, content: `Sorry, I couldn't connect. Please call us on ${BIZ.phone}.` }));
    } finally {
      setBusy(false);
    }
  };

  return { messages, busy, submitted, send, load, setMessages };
};

const Panel = ({ onClose }) => {
  const { messages, busy, submitted, send, load, setMessages } = useChat();
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [messages]);

  const submit = (e) => { e.preventDefault(); const t = text; setText(""); send(t); };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/chat/${sessionId()}/photos`, fd);
      await send("I've attached a photo.", URL.createObjectURL(f));
    } catch (x) {
      setMessages((l) => [...l, { role: "assistant", content: errorText(x) }]);
    } finally {
      setUploading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.96 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-3 bottom-24 top-20 z-50 flex origin-bottom-right flex-col overflow-hidden rounded-[1.5rem] bg-[#F0F5FA] shadow-[0_30px_80px_-20px_rgba(11,31,66,0.55)] ring-1 ring-black/5 md:inset-x-auto md:bottom-24 md:right-6 md:top-auto md:h-[620px] md:max-h-[calc(100vh-8rem)] md:w-[400px]"
      data-testid="chat-panel" role="dialog" aria-label="Chat with Plumberoo"
    >
      <div className="bg-brand flex items-center gap-3 px-5 py-4 text-white">
        <span className="grid h-11 w-11 place-items-center rounded-full bg-white"><img src="/brand/plumberoo-mark.png" alt="" className="h-8 w-8 object-contain" /></span>
        <div className="flex-1 leading-tight">
          <p className="font-display text-lg font-extrabold">Roo · Plumberoo</p>
          <p className="text-xs text-white/85">AI assistant · for urgent jobs call {BIZ.phone}</p>
        </div>
        <button onClick={onClose} aria-label="Close chat" data-testid="chat-close-button" className="grid h-9 w-9 place-items-center rounded-full bg-white/15 hover:bg-white/25"><X className="h-5 w-5" /></button>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-5" data-lenis-prevent data-testid="chat-messages">
        {messages.map((m, i) => <Bubble key={i} m={m} />)}
        {messages.length === 1 && (
          <div className="flex flex-wrap gap-2 pt-1" data-testid="chat-quick-replies">
            {QUICK.map((q) => (
              <button key={q} onClick={() => send(q)} className="rounded-full border border-[#0B55BC]/30 bg-white px-3.5 py-2 text-sm font-semibold text-[#0B55BC] hover:border-[#FF6B00] hover:text-[#E05A00]">{q}</button>
            ))}
          </div>
        )}
        {submitted && (
          <div className="flex items-center gap-2 rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800" data-testid="chat-enquiry-submitted">
            <CheckCircle2 className="h-5 w-5" /> Enquiry sent to the Plumberoo team
          </div>
        )}
        <div ref={endRef} />
      </div>
      <form onSubmit={submit} className="flex items-end gap-2 border-t border-slate-200 bg-white p-3">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="chat-photo-input" />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || busy} aria-label="Attach a photo" data-testid="chat-photo-button"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#F0F5FA] text-[#0B55BC] hover:bg-[#E2ECF8] disabled:opacity-50">
          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
        </button>
        <textarea rows={1} value={text} maxLength={1000} onChange={(e) => setText(e.target.value)} placeholder="Type your message…" data-testid="chat-input"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) submit(e); }}
          className="max-h-28 min-h-[44px] flex-1 resize-none rounded-2xl border border-slate-300 px-4 py-2.5 text-base outline-none focus:border-[#FF6B00]" />
        <button disabled={busy || !text.trim()} aria-label="Send message" data-testid="chat-send-button"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#E05A00] text-white hover:bg-[#C94F00] disabled:opacity-50">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        </button>
      </form>
    </motion.div>
  );
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <AnimatePresence>{open && <Panel onClose={() => setOpen(false)} />}</AnimatePresence>
      <motion.button
        onClick={() => setOpen((o) => !o)} data-testid="chat-launcher-button" aria-label={open ? "Close chat" : "Chat with Plumberoo"}
        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1.6, type: "spring", stiffness: 260, damping: 18 }}
        whileHover={{ y: -3 }}
        className="fixed bottom-[5.5rem] right-4 z-50 flex h-14 items-center gap-2 rounded-full bg-[#E05A00] px-4 font-bold text-white shadow-[0_15px_35px_-10px_rgba(224,90,0,0.8)] md:bottom-6 md:right-6 md:px-5"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
        <span className="hidden md:inline">{open ? "Close" : "Ask Roo"}</span>
      </motion.button>
    </>
  );
};
